import dayjs from "dayjs";
import type { Incident } from "../../../types/incident";
import { hasDetectedAnomalyEvents } from "./incidentChips";
import type { IncidentFolderId } from "./constants";

export type IncidentFilters = {
  searchTerm: string;
  openSearchId: string;
  selectedCustomerId: string | null;
  selectedPriority: string;
  selectedStatus: string;
  selectedAssignee: string;
  selectedGroup: string;
  selectedCategory: string;
  dateRange: { start: string | null; end: string | null };
  sortBy: string;
  sortOrder: "asc" | "desc";
};

export const defaultIncidentFilters: IncidentFilters = {
  searchTerm: "",
  openSearchId: "",
  selectedCustomerId: null,
  selectedPriority: "all",
  selectedStatus: "all",
  selectedAssignee: "all",
  selectedGroup: "all",
  selectedCategory: "all",
  dateRange: { start: null, end: null },
  sortBy: "createdDate",
  sortOrder: "desc",
};

const normalizePriority = (priority: string) => priority?.toUpperCase?.() ?? "";

export function filterByFolder(
  incidents: Incident[],
  folderId: IncidentFolderId,
  currentUsername: string | null,
): Incident[] {
  switch (folderId) {
    case "inbox":
      return incidents.filter(
        (i) =>
          currentUsername &&
          i.assignedTo?.toLowerCase() === currentUsername.toLowerCase(),
      );
    case "group":
      return incidents.filter((i) => i.assignedGroup && i.assignedGroup.trim() !== "");
    case "anomalies":
      return incidents.filter((i) => hasDetectedAnomalyEvents(i));
    case "new":
      return incidents.filter((i) => i.status === "NEW");
    case "critical": {
      const p = (priority: string) => normalizePriority(priority);
      return incidents.filter(
        (i) => p(i.priority) === "CRITICAL" || p(i.priority) === "CRISIS",
      );
    }
    case "resolved":
      return incidents.filter((i) => i.status === "RESOLVED" || i.status === "CLOSED");
    case "all":
    default:
      return incidents;
  }
}

export function applyIncidentFilters(
  incidents: Incident[],
  filters: IncidentFilters,
): Incident[] {
  return incidents
    .filter((incident) => {
      const term = filters.searchTerm.trim().toLowerCase();
      const matchesSearch =
        !term ||
        incident.title?.toLowerCase().includes(term) ||
        incident.description?.toLowerCase().includes(term) ||
        incident.id?.toLowerCase().includes(term) ||
        incident.category?.toLowerCase().includes(term) ||
        incident.assignedTo?.toLowerCase().includes(term);

      const osId = filters.openSearchId.trim().toLowerCase();
      const matchesOpenSearch =
        !osId || incident.openSearchId?.toLowerCase().includes(osId);

      const matchesPriority =
        filters.selectedPriority === "all" ||
        normalizePriority(incident.priority) === filters.selectedPriority;

      const matchesStatus =
        filters.selectedStatus === "all" || incident.status === filters.selectedStatus;

      const matchesAssignee =
        filters.selectedAssignee === "all" ||
        incident.assignedTo === filters.selectedAssignee;

      const matchesGroup =
        filters.selectedGroup === "all" ||
        incident.assignedGroup === filters.selectedGroup;

      const matchesCategory =
        filters.selectedCategory === "all" ||
        incident.category === filters.selectedCategory;

      let matchesDateRange = true;
      if (filters.dateRange.start) {
        matchesDateRange =
          matchesDateRange &&
          dayjs(incident.createdDate).isAfter(dayjs(filters.dateRange.start));
      }
      if (filters.dateRange.end) {
        matchesDateRange =
          matchesDateRange &&
          dayjs(incident.createdDate).isBefore(dayjs(filters.dateRange.end).endOf("day"));
      }

      return (
        matchesSearch &&
        matchesOpenSearch &&
        matchesPriority &&
        matchesStatus &&
        matchesAssignee &&
        matchesGroup &&
        matchesCategory &&
        matchesDateRange
      );
    })
    .sort((a, b) => compareIncidents(a, b, filters.sortBy, filters.sortOrder));
}

function compareIncidents(
  a: Incident,
  b: Incident,
  sortBy: string,
  sortOrder: "asc" | "desc",
): number {
  let aValue: string | number;
  let bValue: string | number;

  switch (sortBy) {
    case "title":
      aValue = (a.title || "").toLowerCase();
      bValue = (b.title || "").toLowerCase();
      break;
    case "priority": {
      const priorityOrder: Record<string, number> = {
        CRISIS: 5,
        CRITICAL: 4,
        HIGH: 3,
        MEDIUM: 2,
        LOW: 1,
      };
      aValue = priorityOrder[normalizePriority(a.priority)] ?? 0;
      bValue = priorityOrder[normalizePriority(b.priority)] ?? 0;
      break;
    }
    case "status":
      aValue = a.status || "";
      bValue = b.status || "";
      break;
    case "assignedTo":
      aValue = a.assignedTo || "";
      bValue = b.assignedTo || "";
      break;
    case "detectedAt":
      aValue = a.detectedAt ? new Date(a.detectedAt).getTime() : 0;
      bValue = b.detectedAt ? new Date(b.detectedAt).getTime() : 0;
      break;
    default:
      aValue = a.createdDate ? new Date(a.createdDate).getTime() : 0;
      bValue = b.createdDate ? new Date(b.createdDate).getTime() : 0;
  }

  if (sortOrder === "asc") {
    return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
  }
  return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
}

export function countActiveFilters(filters: IncidentFilters): number {
  let count = 0;
  if (filters.searchTerm.trim()) count++;
  if (filters.openSearchId.trim()) count++;
  if (filters.selectedCustomerId) count++;
  if (filters.selectedPriority !== "all") count++;
  if (filters.selectedStatus !== "all") count++;
  if (filters.selectedAssignee !== "all") count++;
  if (filters.selectedGroup !== "all") count++;
  if (filters.selectedCategory !== "all") count++;
  if (filters.dateRange.start || filters.dateRange.end) count++;
  return count;
}

export function parseIncidentsResponse(data: unknown): Incident[] {
  if (data && Array.isArray((data as { content?: unknown }).content)) {
    return (data as { content: Incident[] }).content;
  }
  if (Array.isArray(data)) {
    return data as Incident[];
  }
  if (data && Array.isArray((data as { data?: unknown }).data)) {
    return (data as { data: Incident[] }).data;
  }
  if (data && Array.isArray((data as { incidents?: unknown }).incidents)) {
    return (data as { incidents: Incident[] }).incidents;
  }
  return [];
}

export function parseIncidentsPage(data: unknown): { content: Incident[]; totalElements: number } {
  const content = parseIncidentsResponse(data);
  const raw = data && typeof data === "object" ? (data as Record<string, unknown>) : null;
  const page = raw?.page && typeof raw.page === "object" ? (raw.page as Record<string, unknown>) : null;
  const total =
    (typeof page?.totalElements === "number" && page.totalElements) ||
    (typeof raw?.totalElements === "number" && raw.totalElements) ||
    content.length;
  return { content, totalElements: total };
}
